// © 2025 Camp Promethean, LLC. All Rights Reserved.
//
// transcribe.ts — helpers for uploading audio and calling hands-free-transcribe.
//
// Extracted from Recorder.tsx so logic is testable independently and mirrors
// the orchestrate.ts pattern on the backend (raw fetch, explicit auth header).
//
// Two public exports:
//   uploadAudioBlob   — uploads a Blob to the voice-recordings bucket
//   callTranscribe    — calls the hands-free-transcribe edge function

import { supabase } from "@/integrations/supabase/client";
import type { PipelineResult } from "./types";

// ── MIME type / extension negotiation ────────────────────────────────────────

/**
 * Returns the best supported audio MIME type for MediaRecorder on this browser,
 * plus a safe file extension for the storage path.
 *
 * Preference order:
 *   1. audio/webm;codecs=opus  (Chrome, Firefox — smallest, best quality)
 *   2. audio/webm               (Chrome fallback)
 *   3. audio/mp4                (Safari 14.1+ — required on iOS/macOS Safari)
 *   4. null                     (browser does not support MediaRecorder)
 *
 * The extension drives the filename in the storage path and gives Whisper a
 * codec hint via the multipart filename field.
 */
export function getBestAudioMimeType(): {
  mimeType: string;
  extension: string;
} | null {
  if (typeof MediaRecorder === "undefined") return null;

  const candidates: Array<{ mimeType: string; extension: string }> = [
    { mimeType: "audio/webm;codecs=opus", extension: "webm" },
    { mimeType: "audio/webm", extension: "webm" },
    { mimeType: "audio/mp4", extension: "m4a" },
    { mimeType: "audio/ogg;codecs=opus", extension: "ogg" },
  ];

  for (const c of candidates) {
    if (MediaRecorder.isTypeSupported(c.mimeType)) {
      return c;
    }
  }
  return null;
}

// ── Storage path builder ─────────────────────────────────────────────────────

/**
 * Builds the voice-recordings storage path following the spec convention:
 *   {facility_id}/{user_id}/{timestamp}-{uuid}.{extension}
 *
 * Uses crypto.randomUUID() which is available in all modern browsers and the
 * Supabase edge function runtime. No external UUID library needed.
 */
export function buildStoragePath(params: {
  facilityId: string;
  userId: string;
  extension: string;
}): string {
  const { facilityId, userId, extension } = params;
  const timestamp = Date.now();
  const uuid = crypto.randomUUID();
  return `${facilityId}/${userId}/${timestamp}-${uuid}.${extension}`;
}

// ── Upload ───────────────────────────────────────────────────────────────────

export interface UploadResult {
  ok: true;
  storagePath: string;
}

export interface UploadError {
  ok: false;
  error: string;
}

/**
 * Uploads an audio Blob to the private `voice-recordings` Supabase Storage
 * bucket using the authenticated user's session (RLS: upload allowed when
 * path[2] === auth.uid()).
 *
 * Returns the storage path on success, or an error message on failure.
 * Never throws — all errors are caught and reflected in `ok: false`.
 */
export async function uploadAudioBlob(
  blob: Blob,
  storagePath: string,
  mimeType: string,
): Promise<UploadResult | UploadError> {
  try {
    const { error } = await supabase.storage
      .from("voice-recordings")
      .upload(storagePath, blob, {
        contentType: mimeType,
        cacheControl: "3600",
        upsert: false,
      });

    if (error) {
      console.error("[transcribe] Storage upload error:", error);
      return { ok: false, error: `Upload failed: ${error.message}` };
    }

    return { ok: true, storagePath };
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    console.error("[transcribe] Upload exception:", err);
    return { ok: false, error: `Upload failed: ${msg}` };
  }
}

// ── Edge function call ────────────────────────────────────────────────────────

export interface TranscribeCallParams {
  storagePath: string;
  facilityId: string;
  accessToken: string;
  supabaseUrl: string;
  workflowHint?: string;
  context?: Record<string, unknown>;
}

export interface TranscribeNetworkError {
  ok: false;
  httpStatus: 0;
  error: string;
}

export interface TranscribeHttpError {
  ok: false;
  httpStatus: number;
  error: string;
  body: unknown;
}

export interface TranscribeSuccess {
  ok: true;
  httpStatus: number;
  result: PipelineResult;
}

export type TranscribeCallResult =
  | TranscribeSuccess
  | TranscribeHttpError
  | TranscribeNetworkError;

/**
 * POSTs to the hands-free-transcribe edge function.
 *
 * IMPORTANT: HTTP 200 is NOT necessarily full success. The pipeline can return
 * 200 with status = 'parse_failed' or 'resolve_failed' when transcription
 * succeeded but a later step failed. Callers must branch on `result.status`,
 * not just on `ok`.
 *
 * HTTP 5xx = hard failure (transcription itself failed, or server error).
 * HTTP 200 = pipeline reached at least the transcription step.
 *
 * Never throws — all errors are caught and reflected in the return type.
 */
export async function callTranscribe(
  params: TranscribeCallParams,
): Promise<TranscribeCallResult> {
  const { storagePath, facilityId, accessToken, supabaseUrl, workflowHint, context } =
    params;

  let response: Response;
  try {
    response = await fetch(
      `${supabaseUrl}/functions/v1/hands-free-transcribe`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          storage_path: storagePath,
          facility_id: facilityId,
          workflow_hint: workflowHint ?? "auto",
          ...(context ? { context } : {}),
        }),
      },
    );
  } catch (networkErr) {
    const msg =
      networkErr instanceof Error ? networkErr.message : "Network error";
    console.error("[transcribe] Network error:", networkErr);
    return { ok: false, httpStatus: 0, error: msg };
  }

  const httpStatus = response.status;

  let body: unknown;
  try {
    body = await response.json();
  } catch {
    return {
      ok: false,
      httpStatus,
      error: `Server returned HTTP ${httpStatus} with non-JSON body`,
      body: null,
    };
  }

  if (!response.ok) {
    const errBody = body as Record<string, unknown>;
    console.error("[transcribe] Edge function error:", httpStatus, body);
    return {
      ok: false,
      httpStatus,
      error:
        (errBody?.["error"] as string) ??
        `Transcription service returned HTTP ${httpStatus}`,
      body,
    };
  }

  // HTTP 200 — may still be a partial-success pipeline result
  return {
    ok: true,
    httpStatus,
    result: body as PipelineResult,
  };
}

// ── Free-text quick-log (T-203) ───────────────────────────────────────────────

export interface TextLogCallParams {
  text: string;
  facilityId: string;
  accessToken: string;
  supabaseUrl: string;
  /** Omit or pass "auto" to let the intent router pick the workflow. */
  workflowHint?: string;
  context?: Record<string, unknown>;
}

/**
 * POSTs typed text to the hands-free-text edge function. Same return contract as
 * callTranscribe: HTTP 200 may still be a partial-success pipeline result, so
 * callers must branch on `result.status`, not just `ok`.
 *
 * Never throws — all errors are caught and reflected in the return type.
 */
export async function callTextLog(
  params: TextLogCallParams,
): Promise<TranscribeCallResult> {
  const { text, facilityId, accessToken, supabaseUrl, workflowHint, context } = params;

  let response: Response;
  try {
    response = await fetch(`${supabaseUrl}/functions/v1/hands-free-text`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({
        text,
        facility_id: facilityId,
        workflow_hint: workflowHint ?? "auto",
        ...(context ? { context } : {}),
      }),
    });
  } catch (networkErr) {
    const msg = networkErr instanceof Error ? networkErr.message : "Network error";
    console.error("[quicklog] Network error:", networkErr);
    return { ok: false, httpStatus: 0, error: msg };
  }

  const httpStatus = response.status;
  let body: unknown;
  try {
    body = await response.json();
  } catch {
    return {
      ok: false,
      httpStatus,
      error: `Server returned HTTP ${httpStatus} with non-JSON body`,
      body: null,
    };
  }

  if (!response.ok) {
    const errBody = body as Record<string, unknown>;
    console.error("[quicklog] Edge function error:", httpStatus, body);
    return {
      ok: false,
      httpStatus,
      error: (errBody?.["error"] as string) ?? `Quick-log service returned HTTP ${httpStatus}`,
      body,
    };
  }

  return { ok: true, httpStatus, result: body as PipelineResult };
}

// ── Photo / vision input (T-202) ──────────────────────────────────────────────

export interface VisionLogCallParams {
  /** A data URL (data:image/...;base64,...) or raw base64 string. */
  imageBase64: string;
  mimeType: string;
  facilityId: string;
  accessToken: string;
  supabaseUrl: string;
  /** Omit or pass "auto" to let routing pick the workflow. */
  workflowHint?: string;
  context?: Record<string, unknown>;
}

/**
 * POSTs an image to the hands-free-vision edge function. Same return contract as
 * callTranscribe: HTTP 200 may still be a partial-success pipeline result.
 *
 * Never throws — all errors are caught and reflected in the return type.
 */
export async function callVisionLog(
  params: VisionLogCallParams,
): Promise<TranscribeCallResult> {
  const { imageBase64, mimeType, facilityId, accessToken, supabaseUrl, workflowHint, context } = params;

  let response: Response;
  try {
    response = await fetch(`${supabaseUrl}/functions/v1/hands-free-vision`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({
        image_base64: imageBase64,
        mime_type: mimeType,
        facility_id: facilityId,
        workflow_hint: workflowHint ?? "auto",
        ...(context ? { context } : {}),
      }),
    });
  } catch (networkErr) {
    const msg = networkErr instanceof Error ? networkErr.message : "Network error";
    console.error("[photolog] Network error:", networkErr);
    return { ok: false, httpStatus: 0, error: msg };
  }

  const httpStatus = response.status;
  let body: unknown;
  try {
    body = await response.json();
  } catch {
    return {
      ok: false,
      httpStatus,
      error: `Server returned HTTP ${httpStatus} with non-JSON body`,
      body: null,
    };
  }

  if (!response.ok) {
    const errBody = body as Record<string, unknown>;
    console.error("[photolog] Edge function error:", httpStatus, body);
    return {
      ok: false,
      httpStatus,
      error: (errBody?.["error"] as string) ?? `Vision service returned HTTP ${httpStatus}`,
      body,
    };
  }

  return { ok: true, httpStatus, result: body as PipelineResult };
}
